import React from 'react';
import { MoonshotBox } from './MoonshotBox';
import { MoonshotSteps } from 'app/common/data/campaigns';
import { MoonshotSubmitFunction } from './types';
import styles from './MoonshotPlanSelectionFreeTrial.less';
import { Icon, IconName } from 'app/common/components/controls';
import { Noun, Verb, trackUe } from '@trello/analytics';
import { Analytics } from '@trello/atlassian-analytics';
import { useFeatureFlag } from '@trello/feature-flag-client';

interface MoonshotPlanSelectionProps {
  onSubmit: MoonshotSubmitFunction;
}

const features = [
  {
    icon: IconName.Board,
    title: (
      <>
        <strong>Unlimited</strong> Team Boards
      </>
    ),
    description: 'Create Boards for any and all of your team’s needs.',
  },
  {
    icon: IconName.PowerUp,
    title: (
      <>
        <strong>Unlimited</strong> Integrations
      </>
    ),
    description: 'Link Google Drive, Dropbox, and more.',
  },
  {
    icon: IconName.ButlerBot,
    title: (
      <>
        <strong>Advanced</strong> Automations
      </>
    ),
    description: 'Create automagical board and card actions with Butler.',
  },
  {
    icon: IconName.Admin,
    title: (
      <>
        <strong>Assign</strong> Admin roles
      </>
    ),
    description: 'Edit Admin, member, and guest permissions.',
  },
  {
    icon: IconName.Heart,
    title: (
      <>
        <strong>Priority</strong> Support
      </>
    ),
    description: 'Jump the line to get help within hours.',
  },
  {
    icon: IconName.Vote,
    title: (
      <>
        <strong>But Wait...</strong>There’s More!
      </>
    ),
    description: 'Board templates, collections—the list goes on!',
  },
];

export const MoonshotPlanSelectionFreeTrial: React.FC<MoonshotPlanSelectionProps> = ({
  onSubmit,
}) => {
  const sendGASEvent = useFeatureFlag('dataeng.gasv3-event-tracking', false);

  const onContinueWithTracking = () => {
    onSubmit(MoonshotSteps.PAYMENT_FORM);
    trackUe({
      category: Noun.MOONSHOT,
      verb: Verb.SUBMITS,
      directObj: Noun.BUSINESS_CLASS_FREE_TRIAL_UPSELL,
    });

    if (sendGASEvent) {
      Analytics.sendTrackEvent({
        action: 'submitted',
        actionSubject: 'upsell',
        actionSubjectId: 'bcFreeTrialUpsell',
        source: 'moonshotBCFreeTrialUpsellScreen',
      });
    }
  };

  const onSkipWithTracking = () => {
    onSubmit(MoonshotSteps.SUCCESS);
    trackUe({
      category: Noun.MOONSHOT,
      verb: Verb.SKIPS,
      directObj: Noun.BUSINESS_CLASS_FREE_TRIAL_UPSELL,
    });

    if (sendGASEvent) {
      Analytics.sendTrackEvent({
        action: 'skipped',
        actionSubject: 'upsell',
        actionSubjectId: 'bcFreeTrialUpsell',
        source: 'moonshotBCFreeTrialUpsellScreen',
      });
    }
  };

  return (
    <>
      <MoonshotBox className={styles.upperBox}>
        <div className={styles.content}>
          <div className={styles.exclusiveOffer}>
            <img
              src={require('resources/images/moonshot/exclusive-offer.png')}
              alt=""
            />
            <span>Exclusive Offer</span>
          </div>
          <h1>
            <img
              src={require('resources/images/moonshot/bc-sparkle.svg')}
              alt=""
            />
            You've got Business Class!
          </h1>
          <p>
            Trello Business Class is built for teams who get things done. For 30
            days, your team can use all of Trello's Business Class features for
            free!
          </p>
          <div className={styles.features}>
            {features.map(({ icon, title, description }, i) => (
              <div className={styles.feature} key={`feature-${i}`}>
                <Icon name={icon} small className={styles.featureIcon} />
                <h3>{title}</h3>
                <p>{description}</p>
              </div>
            ))}
          </div>
          <button
            className={styles.startTrialButton}
            onClick={onContinueWithTracking}
          >
            Start 30-day free trial
          </button>
          <p className={styles.quiet}>
            Don’t worry, you’ll always have the option to use the free version
            of Trello.
          </p>
        </div>
        <div className={styles.trustedBy}>
          <span>Trusted by teams at</span>
          <img
            src={require('resources/images/moonshot/fender.png')}
            alt="Fender"
            width="45"
          />
          <img
            src={require('resources/images/moonshot/the-new-york-times.png')}
            alt="The New York Times"
            width="137"
          />
          <img
            src={require('resources/images/moonshot/fortnite.png')}
            alt="Fortnite"
            width="58"
          />
        </div>
      </MoonshotBox>
      <button className={styles.startFreeButton} onClick={onSkipWithTracking}>
        Start without Business Class
      </button>
    </>
  );
};
